import { useListStore } from "@/store/listStore";
import { useEventStore } from "@/store/useEventStore";
import { useGlobalErrorStore } from "@/store/useGlobalError";
import { pb } from "@/utils/PocketBaseAdapter";
import { useUserStore } from "@/utils/UserStore";
import { moment } from "@/utils/useTimeZone";
import isEmpty from "lodash/isEmpty";

export const useEvent = () => {
  // Store
  const eventStore = useEventStore();
  const globalError = useGlobalErrorStore();
  const userStore = useUserStore();
  const list = useListStore();

  const { handleEventEdit } = eventStore;
  const { listArea, activityMap } = storeToRefs(list);
  const {
    eventBookingsId,
    eventId,
    recurringEventId,
    isEventPending,
    findSoldTicket,
  } = storeToRefs(eventStore);
  const { isBookingWarningDialogVisible, errorDateList } =
    storeToRefs(globalError);
  const { currentLocation } = storeToRefs(userStore);

  // Data
  const paymentOptions = ref<any[]>([
    {
      title: "Coupon / Gift voucher",
      value: "couponOrGift",
    },
    {
      title: "User Credit",
      value: "userCredit",
    },
    {
      title: "POS",
      value: "pos",
    },
    {
      title: "Waive Fee",
      value: "waiveFee",
    },
  ]);
  const daysOfWeek = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  const recurringWeeks = ref([
    { id: 1, label: "M", value: "Monday" },
    { id: 2, label: "T", value: "Tuesday" },
    { id: 3, label: "W", value: "Wednesday" },
    { id: 4, label: "T", value: "Thursday" },
    { id: 5, label: "F", value: "Friday" },
    { id: 6, label: "S", value: "Saturday" },
    { id: 7, label: "S", value: "Sunday" },
  ]);
  const totalPaidAmount = ref<number>(0);

  // computed
  const recurringEndDateConfig = computed(() => (maxWeek: number) => {
    // Calculate the maximum date using Moment.js
    const maxDate = moment().add(maxWeek * 7, "days");
    return {
      dateFormat: "d-m-Y",
      disable: [
        function (date: any) {
          const today = moment().startOf("day");
          const momentDate = moment(date).startOf("day");
          // Disable past dates or dates beyond the max week range
          return momentDate.isBefore(today) || momentDate.isAfter(maxDate);
        },
      ],
    };
  });

  const getSelectedRecurringItem = computed(() => (type: string) => {
    // Map recurringWeeks by id
    return recurringWeeks.value.reduce((acc: any, item) => {
      acc[item.id] = item;
      return acc;
    }, {});
  });

  const alternativeAreaList = computed(() => (activityId: string) => {
    return listArea.value?.filter(
      (area: any) =>
        area.activities?.includes(activityId) &&
        area.location === currentLocation.value
    );
  });

  const getRecurringDates = computed(() => (config: any) => {
    const { days, endDate, frequency, interval } = config;
    const recurringItems = getSelectedRecurringItem.value(interval);
    const endMoment = moment(endDate).startOf("day");
    const today = moment().startOf("day");
    const recurringConfigDates: string[] = [];

    // Generate valid booking dates based on the selected days
    while (today.isSameOrBefore(endMoment)) {
      days.forEach((dayId: string) => {
        const dayOfWeek = recurringItems[dayId]?.value;
        if (dayOfWeek) {
          const targetDate = today.clone().day(dayOfWeek);

          // Adjust to the next week's day if the target date is in the past
          const nextValidDate = targetDate.isBefore(today)
            ? targetDate.add(1, "week")
            : targetDate;

          // Add the date to the list if it's within the end date range
          if (nextValidDate.isSameOrBefore(endMoment)) {
            recurringConfigDates.push(nextValidDate.format("YYYY-MM-DD"));
          }
        }
      });

      // Move to the next frequency period
      today
        .add(frequency, interval === "Weeks" ? "week" : "month")
        .startOf("day");
    }

    return recurringConfigDates;
  });

  // Method
  const mapSelectionToMinutes = async (selectedValue: any) => {
    // Ensure the input is a string
    selectedValue = selectedValue.toString();

    // If the input is a whole number (e.g., "1"), treat it as hours
    if (!selectedValue.includes(".")) {
      return Number(selectedValue) * 60;
    }

    // Split the time string and convert to minutes
    const [hours = "0", minutes = "0"] = selectedValue.split(".").map(Number);
    return hours * 60 + minutes;
  };

  const getDuration = (duration: any, getBookingSpacing: number) => {
    // Ensure duration and bookingSpacing are positive numbers
    if (duration < 0 || getBookingSpacing <= 0) {
      throw new Error(
        "Duration must be a positive number and bookingSpacing must be greater than 0"
      );
    }

    // Convert duration to minutes if it's not already
    const durationMinutes =
      typeof duration === "number" ? duration : Math.floor(duration / 60000);

    // Round the duration to the nearest booking spacing
    const roundedMinutes =
      Math.round(durationMinutes / getBookingSpacing) * getBookingSpacing;

    // Convert roundedMinutes to hours and minutes
    const hours = Math.floor(roundedMinutes / 60);
    const minutes = roundedMinutes % 60;

    // Return formatted duration
    return `${hours}:${minutes.toString().padStart(2, "0")}`;
  };

  const reArrangeBookingOnActivity = (bookings: any[]) => {
    // Sort the bookings
    const sortedBookings = bookings?.sort((a: any, b: any) => {
      // Sort by activity ID first
      if (a.activity < b.activity) return -1;
      if (a.activity > b.activity) return 1;

      // If activity IDs are the same, sort by areas from the area
      const areaNumberA = a.area;
      const areaNumberB = b.area;
      if (areaNumberA !== areaNumberB) {
        return areaNumberA - areaNumberB;
      }
      // Default return value when both activity and areas are equal
      return 0;
    });

    return sortedBookings;
  };

  // Helper function to calculate docket line values
  const calculateLineValues = (
    activity: string,
    value: number,
    discount: number
  ) => {
    const activityData = activityMap.value[activity] || {};
    const bookingSpacing = activityData.bookingSpacing || 1;
    const discountAmount = (value * discount) / 100;
    const lineTotal = value - discountAmount;

    return { bookingSpacing, discountAmount, lineTotal };
  };

  const refetchEvent = async () => {
    const eventData = await pb.collection("events").getOne(eventId.value, {
      expand: "bookings, bookings.docketLine.docket",
    });
    handleEventEdit(eventData);
  };

  const confirmDelete = async () => {
    try {
      const { expand: { bookings, products_via_event } = {} } = await pb.collection("events").getOne(eventId.value, {
        expand: "bookings, products_via_event",
      });

      if (bookings?.length) {
        const deletePromises = bookings?.map((booking: any) => {
          return pb.collection("bookings").update(booking.id, { state: "Deleted" });
        });

        await Promise.all(deletePromises);
      }

      if (products_via_event?.length) {
        await Promise.all(
          products_via_event.map((product: any) =>
            pb.collection("products").update(product.id, { inactive: true })
          )
        );
      }

      await pb.collection("events").update(eventId.value, { state: "Deleted" });

      // Remove this event from its parent recurring event record
      try {
        const recurringRecords = await pb.collection("recurringEvents").getList(1, 1, {
          filter: `events ~ '${eventId.value}'`,
        });
        if (recurringRecords?.items?.length) {
          const recurringRecord = recurringRecords.items[0];
          const updatedEvents = recurringRecord.events.filter((eid: string) => eid !== eventId.value);
          if (updatedEvents.length) {
            await pb.collection("recurringEvents").update(recurringRecord.id, { events: updatedEvents });
          } else {
            await pb.collection("recurringEvents").delete(recurringRecord.id);
          }
        }
      } catch (e) {
        console.error("Failed to update recurring event record:", e);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const confirmDeleteAllRecurring = async (recurringId: string) => {
    try {
      const recurringRecord = await pb.collection("recurringEvents").getOne(recurringId, {
        expand: "events.bookings,events.products_via_event",
      });

      const events = recurringRecord?.expand?.events || [];

      for (const event of events) {
        const bookings = event?.expand?.bookings || [];
        for (const booking of bookings) {
          await pb.collection("bookings").update(booking.id, { state: "Deleted" });
        }

        const products = event?.expand?.products_via_event || [];
        for (const product of products) {
          await pb.collection("products").update(product.id, { inactive: true });
        }

        await pb.collection("events").update(event.id, { state: "Deleted" });
      }

      await pb.collection("recurringEvents").delete(recurringId);
    } catch (e) {
      console.error(e);
    }
  };

  const removeExistingPendingBooking = async () => {
    try {
      const { expand: { bookings } = {} } = await pb.collection("events").getOne(eventId.value, { expand: "bookings" });

      if (bookings?.length && bookings[0]?.state === "Pending") {
        const deletePromises = bookings?.map((booking: any) => {
          return pb.collection("bookings").update(booking.id, { state: "Deleted" });
        });
        await Promise.all(deletePromises);
        console.log('delete pending', eventId.value)
        await pb.collection("events").update(eventId.value, { state: "Deleted" });
      }
      return;
    } catch (e) {console.error(e)}
  };

  const reserveSlot = async (params: any) => {
    const { duration, date, time, activity, visitorEmail, owner, userId, numberInParty } = params;

    // Convert duration to minutes
    const durationTotal = await mapSelectionToMinutes(
      String(duration)?.replace(":", ".")
    );

    // Create ISO start time from date and time
    const startTime = moment(
      `${date} ${time}`,
      "YYYY-MM-DD HH:mm"
    ).toISOString();

    // Variable to store all booking IDs
    let bookingIds: string[] = [];

    // Iterate over the activities object
    for (const activityId in activity) {
      const bayIds = activity[activityId];

      // Loop through each bayId and create booking
      for (const bayId of bayIds) {
        const due =
          (durationTotal / activityMap.value[activityId]?.bookingSpacing) *
          activityMap.value[activityId]?.sessionFullPrice;

        try {
          // Create booking
          const booking = await pb.collection("bookings").create({
            startTime: startTime,
            duration: durationTotal,
            area: bayId,
            visitorEmail: visitorEmail,
            owner: owner,
            numberInParty: bookingIds.length === 0 ? numberInParty : undefined,
            activity: activityId,
            value: params?.trackTickets ? 0 : due,
            discount: 0,
            due: params?.trackTickets ? 0 : due,
            state: "Pending",
            createdBy: userId,
            source: "Event",
          });
          bookingIds.push(booking.id);
        } catch (bookingError) {
          // If booking creation fails, rollback all previously created bookings
          await rollbackBookings(bookingIds);
          throw bookingError;
        }
      }
    }
    eventBookingsId.value = bookingIds;

    // create an event
    const eventData = await pb.collection("events").create({
      bookings: eventBookingsId.value,
      trackTickets: params?.trackTickets,
      location: currentLocation.value,
      state: "Pending",
    });

    eventId.value = eventData.id;

    isEventPending.value = true;
  };

  // Helper function to delete all created bookings if fail mid way
  const rollbackBookings = async (bookingIds: string[]) => {
    for (const bookingId of bookingIds) {
      try {
        await pb.collection("bookings").update(bookingId, { state: "Deleted" });
      } catch (deleteError) {
        console.error(`Failed to delete booking ${bookingId}:`, deleteError);
      }
    }
  };

  const updateEvent = async (params: any) => {
    try {
      const {
        name,
        description,
        moreInfoUrl,
        activity,
        date,
        time,
        duration,
        visitorEmail,
        owner,
        numberInParty,
        updatedBy,
        trackTickets,
        ticketsConfig,
        optionsConfig,
        docketId,
        oldTicketsConfig,
        todAdjustment,
        docketDiscount,
        lineDiscountTotal,
        discountApplied,
        activityDiscounts // Map of activity-specific discount data
      } = params;

      // Convert duration to minutes and create ISO start time
      let durationTotal = await mapSelectionToMinutes(
        String(duration)?.replace(":", ".")
      );

      const startTime = moment(
        `${date} ${time}`,
        "YYYY-MM-DD HH:mm"
      ).toISOString();

      // Fetch all current bookings for the event
      const eventData = await pb.collection("events").getOne(eventId.value, {
        expand: "bookings",
      });

      const existingBookings = eventData?.expand?.bookings || [];
      const existingBookingIds = existingBookings.map((b: any) => b.id);

      // Check for booking conflicts
      for (const activityId in activity) {
        const bayIds = activity[activityId];
        
        for (const bayId of bayIds) {
          // Check if this time slot is available (excluding current event's bookings)
          try {
            const endTime = moment(startTime).add(durationTotal, 'minutes').toISOString();
            
            const conflictFilter = `
              area = "${bayId}" &&
              activity = "${activityId}" &&
              state != "Deleted"
            `.replace(/\s+/g, ' ').trim();
            
            const conflictingBookings = await pb.collection("bookings").getFullList({
              filter: conflictFilter,
            });

            // Filter out bookings that belong to this event AND check for time overlap
            const externalConflicts = conflictingBookings.filter(
              (booking: any) => {
                // Skip if it's one of our existing bookings
                if (existingBookingIds.includes(booking.id)) {
                  return false;
                }
                
                // Check for time overlap
                const bookingStart = moment(booking.startTime);
                const bookingEnd = moment(booking.endTime);
                const newStart = moment(startTime);
                const newEnd = moment(endTime);
                
                // Two time ranges overlap if:
                // - new booking starts before existing ends AND
                // - new booking ends after existing starts
                const hasOverlap = newStart.isBefore(bookingEnd) && newEnd.isAfter(bookingStart);
                
                return hasOverlap;
              });

            if (externalConflicts.length > 0) {
              throw {
                response: {
                  message: "Booking conflict detected in the same area."
                }
              };
            }
          } catch (error: any) {
            // If it's our custom error, throw it
            if (error?.response?.message) {
              throw error;
            }
            // Otherwise, log and continue (might be a network error)
            console.error('Error checking conflicts:', error);
          }
        }
      }

      // Flatten the activity data to determine required bookings
      const multiAreasBookings = Object.entries(activity).flatMap(
        ([activityId, bayIds]: any) =>
          bayIds.map((bayId: string) => ({ activityId, bayId }))
      );

      const bookingsToUpdate = [];
      const bookingsToCreate = [];
      const bookingsToDelete = new Set(existingBookingIds); // Hold All Bookings That Exist

      let foundUnPaid = false
      let bookingsToPaid:any = {}

      let docketIDCheck = docketId
      let newDocketDue = 0;
      let newDocketTotal = 0;

      for (const [index, requiredBooking] of multiAreasBookings.entries()) {

        const existingBooking = existingBookings.filter(eb => requiredBooking.activityId == eb.activity && requiredBooking.bayId == eb.area)[0] || null

        // Get activity data
        const activityData =
          activityMap.value[
            existingBooking?.activity || requiredBooking.activityId
          ] || {};

        // Get activity-specific discount data for this booking's activity
        const activityDiscount = activityDiscounts?.[requiredBooking.activityId] || {
          todAdjustment: todAdjustment || 0,
          docketDiscount: docketDiscount || 0,
          discountApplied: discountApplied || "None",
          lineDiscountTotal: lineDiscountTotal || 0
        };

        // Use the maximum discount from TOD and docket discounts for THIS activity (ignore increases)
        const discountPercentage = Math.max(
          activityDiscount.todAdjustment < 0 ? -activityDiscount.todAdjustment : 0,
          activityDiscount.docketDiscount
        );

        // Calculate values
        const bookingSpacing = activityData.bookingSpacing || 1;
        const sessionFullPrice = activityData.sessionFullPrice || 60;
        const dynamicValue = trackTickets ? 0 : (durationTotal / bookingSpacing) * sessionFullPrice;
        const priceAfterTodIncrease = activityDiscount.todAdjustment > 0 ? dynamicValue + (dynamicValue * activityDiscount.todAdjustment / 100) : dynamicValue;


        let dynamicDue = 0;
        if (!trackTickets) {
          const discountAmount = (priceAfterTodIncrease * discountPercentage) / 100;
          const discountedValue = priceAfterTodIncrease - discountAmount;
          const paidAmount = existingBooking?.paid || 0;
          dynamicDue = Math.max(discountedValue - paidAmount, 0);
          newDocketDue += dynamicDue
        }

        if (existingBooking) {
          bookingsToPaid[existingBooking.id] = existingBooking?.paid || 0
          //if(!foundUnPaid) {
            let docketPaidCheck = await pb.collection("docketLines").getFullList({
              filter: 'transactionType="Event" && booking="' + existingBooking.id + '"',
              expand: "docket",
              fields: "docket,expand.docket.status,qty,id"
            })

            // Find and Move To UnPaid Docket
            if (docketPaidCheck) {
              docketPaidCheck.forEach((docketPaid) => {
                if (docketPaid?.expand?.docket?.status != "Paid" && docketPaid?.expand?.docket?.status != "Closed") {
                  docketIDCheck = docketPaid?.docket
                  foundUnPaid = true
                  existingBooking.docketLine = docketPaid.id
                }
              })
            }
          //}

          // Update existing booking
          bookingsToUpdate.push({
            id: existingBooking.id,
            area: requiredBooking.bayId,
            activity: requiredBooking.activityId,
            startTime,
            duration: durationTotal,
            visitorEmail,
            owner,
            numberInParty: index === 0 ? numberInParty : undefined,
            value: dynamicValue,
            due: dynamicDue,
            docketLine: existingBooking?.docketLine,
            discount: discountPercentage,
            visitors: name,
            state: "Confirmed",
            updatedBy,
          });
          bookingsToDelete.delete(existingBooking.id); // Mark as updated
        } else {
          // Create new booking
          bookingsToCreate.push({
            startTime,
            duration: durationTotal,
            area: requiredBooking.bayId,
            owner,
            visitorEmail,
            numberInParty: index === 0 ? numberInParty : undefined,
            activity: requiredBooking.activityId,
            value: dynamicValue,
            discount: discountPercentage,
            due: dynamicDue,
            visitors: name,
            state: "Confirmed",
            createdBy: updatedBy,
            source: "Event",
          });
          // Track total value for new bookings (for new docket total)
          newDocketTotal += dynamicValue;
        }
      }

      if(docketIDCheck && !foundUnPaid && docketId && newDocketDue != 0) {
        // docket is Paid - Update for Line Items
        // Get Last Docket
        let lastDocket = await pb.collection("dockets").getOne(docketId)

        let newDocketParams:any = {}
        newDocketParams.user = lastDocket?.user || ''
        newDocketParams.location = lastDocket?.location || ''
        newDocketParams.description = lastDocket?.description || ''
        newDocketParams.createdBy = lastDocket?.createdBy || ''
        newDocketParams.updatedBy = lastDocket?.updatedBy || ''
        newDocketParams.source = lastDocket?.source || ''
        newDocketParams.discountEvents = lastDocket?.discountEvents || ''
        newDocketParams.discCodeEvents = lastDocket?.discCodeEvents || ''
        newDocketParams.discManEvents = lastDocket?.discManEvents || ''
        newDocketParams.discMemEvents = lastDocket?.discMemEvents || ''

        let newDocket = await pb.collection("dockets").create(newDocketParams)
        docketIDCheck = newDocket.id

      }

      // Perform updates
      for (const update of bookingsToUpdate) {
        try {
          await pb.collection("bookings").update(update.id, update);
        } catch(e){}

        if (update?.docketLine) {
          const sessionPrice = activityMap.value[update?.activity]?.sessionFullPrice || update.value

          const { lineTotal, bookingSpacing } = calculateLineValues(
            update.activity,
            update.value,
            update.discount
          );

          const qty = Math.ceil(durationTotal / bookingSpacing)

          // Get activity-specific discount data
          const activityDiscount = activityDiscounts?.[update.activity] || {
            todAdjustment: todAdjustment,
            docketDiscount: docketDiscount,
            discountApplied: discountApplied,
            lineDiscountTotal: lineDiscountTotal
          };

          if(docketIDCheck && foundUnPaid) {
            try {
              let docketCheck = await pb.collection("docketLines").getFirstListItem('docket="' + docketIDCheck + '" && booking="' + update.id + '"')
              if(docketCheck){
                update.docketLine = docketCheck.id
              }
            } catch(e){
              console.error('error', e)
            }

            await pb.collection("docketLines").update(update.docketLine, {
              qty: qty,
              itemPrice: sessionPrice,
              lineTotal: lineTotal,
              docketDiscount: activityDiscount.docketDiscount,
              todAdjustment: activityDiscount.todAdjustment,
              discountApplied: activityDiscount.discountApplied,
              lineDiscountTotal: activityDiscount.lineDiscountTotal,
            });
          } else if (docketIDCheck && !foundUnPaid) {
            // Only bookings that still owe money need a line on the new
            // overflow docket — an already fully paid booking's old line sits
            // on the locked (Paid/Closed) docket and must be left untouched.
            if(qty > 0 && update.due > 0) {
              const newLine = await pb.collection("docketLines").create({
                qty: qty,
                itemPrice: sessionPrice,
                lineTotal: lineTotal,
                docketDiscount: activityDiscount.docketDiscount,
                todAdjustment: activityDiscount.todAdjustment,
                discountApplied: activityDiscount.discountApplied,
                lineDiscountTotal: activityDiscount.lineDiscountTotal,
                docket: docketIDCheck,
                transactionType: "Event",
                status: "Available",
                booking: update.id,
              });

              await pb.collection("bookings").update(update.id, { docketLine: newLine.id });
            }
          }
        }
      }

      // Perform creations
      for (const create of bookingsToCreate) {
        let docketLineId = null;

        if (docketIDCheck) {
          const { bookingSpacing, lineTotal } = calculateLineValues(
            create.activity,
            create.value,
            create.discount
          );

          // Get activity-specific discount data
          const activityDiscount = activityDiscounts?.[create.activity] || {
            todAdjustment: todAdjustment,
            docketDiscount: docketDiscount,
            discountApplied: discountApplied,
            lineDiscountTotal: lineDiscountTotal
          };

          const sessionPrice = activityMap.value[create?.activity]?.sessionFullPrice || create.value
          const docketLine = await pb.collection("docketLines").create({
            docket: docketIDCheck,
            transactionType: "Event",
            qty: durationTotal / bookingSpacing,
            itemPrice: sessionPrice,
            lineTotal,
            docketDiscount: activityDiscount.docketDiscount,
            todAdjustment: activityDiscount.todAdjustment,
            discountApplied: activityDiscount.discountApplied,
            lineDiscountTotal: activityDiscount.lineDiscountTotal,
            status: "Available",
          });

          docketLineId = docketLine.id;
        }

        const booking = await pb.collection("bookings").create({
          ...create,
          docketLine: docketLineId,
        });
        existingBookingIds.push(booking.id);

        if (docketLineId) {
          await pb.collection("docketLines").update(docketLineId, {
            booking: booking.id,
          });
        }
      }

      for (const idToDelete of bookingsToDelete) {

        // Fetch the booking to check if it has a docketLine
        const bookingToDelete = existingBookings.find(
          (b: any) => b.id === idToDelete
        );

        if (bookingToDelete?.docketLine) {
          // Delete the associated docketLine
          try {
            await pb.collection("docketLines").delete(bookingToDelete.docketLine);
          } catch(e){}
        }

        // delete the Booking
        try {
          await pb.collection("bookings").update(idToDelete as string, { state: "Deleted" });
        } catch(e){
          console.error("Error deleting booking:", e)
        }

        // Remove the booking ID from the existingBookingIds array
        const index = existingBookingIds.indexOf(idToDelete);
        if (index !== -1) existingBookingIds.splice(index, 1);

      }

      isEventPending.value = false;

      const eventLocation = eventData.location;

      // Update products first so they're written before the event update triggers the subscription
      if (oldTicketsConfig?.length) {
        const removeEventsFrom = oldTicketsConfig.filter((oldItem: any) => {
          return !ticketsConfig.some(
            (newItem: any) => newItem.id === oldItem.id
          );
        });

        for (const product of removeEventsFrom) {
          await pb.collection("products").update(product.id, {inactive: true});
        }
      }

      if (ticketsConfig?.length) {
        const newItems = ticketsConfig.filter((item: any) => !item.id);
        let eventCategory: any = null;
        if (newItems.length) {
          eventCategory = await pb.collection("productCategories").getFirstListItem(`name="Events" && location="${eventLocation}"`, { requestKey: null }).catch(() => null);
          if (!eventCategory) {
            eventCategory = await pb.collection("productCategories").create({
              name: "Events",
              location: eventLocation,
              icon: 'mdi-calendar-star',
              isVoucher: true,
              itemType: "Event",
              usersCantDelete: true
            });
          }
        }

        for (const item of ticketsConfig) {
          if (item.reportingGroup && typeof item.reportingGroup === "object") {
            item.reportingGroup = (item.reportingGroup as any).id;
          }

          if(!item.id) {
            const newProduct = await pb.collection("products").create({
              ...item,
              eventDate: startTime,
              event: eventId.value,
              location: eventLocation,
              category: eventCategory.id,
              inStock: item.eventTicketLimit,
              salesEnd: item.salesEnd ? moment(item.salesEnd).toPBString() : "",
            });
            item.id = newProduct.id
          } else {
            const soldTickets = findSoldTicket.value(item.id);
            let inStock = item?.eventTicketLimit;
            if(inStock != -1){
              inStock = Math.max(item?.eventTicketLimit - soldTickets, 0);
            }

            await pb.collection("products").update(item.id, {
              ...item,
              eventDate: startTime,
              salesEnd: item.salesEnd ? moment(item.salesEnd).toPBString() : "",
              inStock,
            });
          }
        }
      }

      // Get existing options
      const exsistingOptions = await pb.collection("eventOptions").getFullList({
        filter: `event = "${eventId.value}"`,
      });

      const optionsToRemove = exsistingOptions.filter((option: any) => {
        return !optionsConfig.some(
          (newItem: any) => newItem.id === option.id
        );
      });

      // Remove any options that are not in the new config
      for (const option of optionsToRemove) {
        await pb.collection("eventOptions").delete(option.id);
      }

      // Create new options and update existing options
      if(optionsConfig?.length) {
        for (const option of optionsConfig) {
          if(!option.id) {
            const newOption = await pb.collection("eventOptions").create({
              ...option,
              event: eventId.value,
            });
            option.id = newOption.id
          } else {
            await pb.collection("eventOptions").update(option.id, option);
          }
        }
      }

      // Update event record last — this triggers the real-time subscription in EventList,
      // so products and options must already be saved before this fires.
      const updatedEventData = await pb.collection("events").update(
        eventId.value,
        {
          bookings: existingBookingIds,
          name,
          description,
          moreInfoUrl,
          updatedBy,
          trackTickets,
          state: "Confirmed",
        },
        {
          expand: "bookings.docketLine.docket",
        }
      );

      handleEventEdit(updatedEventData);
    } catch (e) {
      console.error(e);
      return e;
    }
  };

  const handleRecurringEventUpdate = async (
    booking: any,
    recurringConfigDates: string[],
    eventList?: any,
    recurringId?: string,
    endDate?: string
  ) => {
    errorDateList.value = [];
    const errorDates: string[] = [];
    const bookingCombinations = Object.entries(booking.activity)?.flatMap(
      ([activityId, areaIds]) =>
        (areaIds as string[])?.map((areaId) => ({ areaId, activityId }))
    );

    const durationTotal = await mapSelectionToMinutes(
      String(booking?.duration)?.replace(":", ".")
    );

    // Closure function
    const createOrUpdateBooking = async (
      areaId: string,
      activityId: string,
      bookingDate: string,
      bookingParam: any,
      isFirstBooking: boolean
    ): Promise<string> => {
      let bookingId = "";

      const discountPercentage = bookingParam?.discount || 0;
      const paidAmount = bookingParam?.paid || 0;

      // Extract activity details
      const activityDetails = activityMap.value[activityId] || {};
      const bookingSpacing = activityDetails?.bookingSpacing || 1;
      const sessionFullPrice = activityDetails?.sessionFullPrice || 0;

      // Calculate the total value
      const dynamicValue = (durationTotal / bookingSpacing) * sessionFullPrice;

      // Calculate the discount amount and final due
      const discountAmount = (dynamicValue * discountPercentage) / 100;
      const discountedValue = dynamicValue - discountAmount;
      const dynamicDue = Math.max(discountedValue - paidAmount, 0);

      const bookingPayload = {
        startTime: moment(
          `${bookingDate} ${booking.time}`,
          "YYYY-MM-DD HH:mm"
        ).toISOString(),
        duration: durationTotal,
        area: areaId,
        activity: activityId,
        discount: discountPercentage,
        due: dynamicDue,
        value: dynamicValue,
        state: "Confirmed",
        owner: booking?.owner,
        visitorEmail: booking.visitorEmail,
        numberInParty: isFirstBooking ? booking.numberInParty : undefined,
        createdBy: booking?.loggedInUser,
        source: "Event",
      };

      if (bookingParam.id) {
        // Update existing booking
        try{
          const updatedBooking = await pb.collection("bookings").update(bookingParam.id, bookingPayload);
          bookingId = updatedBooking?.id;
        } catch(e){
          errorDates.push(bookingDate);
          bookingId = bookingParam.id; // Keep the original booking so it isn't deleted as "unused"
        }
      } else {
        // Create new booking
        try{
          const newBooking = await pb.collection("bookings").create(bookingPayload);
          bookingId = newBooking?.id;
        } catch(e){
          errorDates.push(bookingDate); // Track the date where error occurred
        }
      }
      return bookingId;
    };

    // Closure function
    const handleBookingCreation = async (
      areaId: string,
      activityId: string,
      bookingDate: string,
      singleBooking: any = {},
      isFirstBooking: boolean = false
    ): Promise<string | null> => {
      const bookingId = await createOrUpdateBooking(
        areaId,
        activityId,
        bookingDate,
        singleBooking,
        isFirstBooking
      );
      return bookingId || null;
    };

    // Start of handleRecurringEventUpdate
    const { events } = eventList?.expand;
    const eventIds: string[] = [];

    // Build a map of existing events keyed by their booking date
    const eventsByDate = new Map<string, any>();
    const orphanedEventIds: string[] = [];
    for (const event of events) {
      const bookingStartTime = event?.expand?.bookings?.[0]?.startTime;
      if (bookingStartTime) {
        const dateKey = moment(bookingStartTime).format("YYYY-MM-DD");
        eventsByDate.set(dateKey, event);
      } else {
        orphanedEventIds.push(event.id);
      }
    }

    // Save original booking data for rollback
    const originalBookingSnapshots = new Map<string, any>();
    for (const [, event] of eventsByDate) {
      for (const b of event?.expand?.bookings || []) {
        originalBookingSnapshots.set(b.id, { ...b });
      }
    }

    // Track changes for rollback
    const updatedBookingIds: string[] = [];
    const newlyCreatedBookingIds: string[] = [];
    const newlyCreatedEventIds: string[] = [];
    let hasConflict = false;

    // Defer cleanup until we know all bookings succeeded
    const deferredUnusedBookings: string[] = [];
    const deferredEventUpdates: { eventId: string; bookingIds: string[] }[] = [];

    // Loop for each date
    for (const bookingDate of recurringConfigDates) {
      if (hasConflict) break;

      const existedEvent = eventsByDate.get(bookingDate);
      if (existedEvent) eventsByDate.delete(bookingDate);
      const { bookings } = existedEvent?.expand || {};
      const sortedBookings = reArrangeBookingOnActivity(bookings);
      const bookingIds: string[] = [];

      if (existedEvent) {
        // Update existing event's bookings
        for (const [
          index,
          { areaId, activityId },
        ] of bookingCombinations.entries()) {
          const discountOfAnyExistingBooking = sortedBookings?.find(
            (booking: any) => booking?.discount || {}
          );
          const singleBooking = sortedBookings[index] || {};
          singleBooking.discount = discountOfAnyExistingBooking?.discount || 0;
          const bookingId = await handleBookingCreation(
            areaId,
            activityId,
            bookingDate,
            singleBooking,
            bookingIds.length === 0
          );

          if (errorDates.length) {
            hasConflict = true;
            break;
          }

          if (bookingId) {
            bookingIds.push(bookingId);
            if (singleBooking.id) {
              updatedBookingIds.push(singleBooking.id);
            } else {
              newlyCreatedBookingIds.push(bookingId);
            }
          }
        }

        if (!hasConflict) {
          // Collect unused bookings to delete later
          const unusedBookings = sortedBookings?.filter(
            (b: any) => !bookingIds?.includes(b.id)
          );
          for (const ub of unusedBookings || []) {
            deferredUnusedBookings.push(ub.id);
          }
          deferredEventUpdates.push({ eventId: existedEvent.id, bookingIds });
          eventIds.push(existedEvent.id);
        }
      } else {
        // Create new event with bookings
        for (const { areaId, activityId } of bookingCombinations) {
          const bookingId = await handleBookingCreation(
            areaId,
            activityId,
            bookingDate,
            {},
            bookingIds.length === 0
          );

          if (errorDates.length) {
            hasConflict = true;
            break;
          }

          if (bookingId) {
            bookingIds.push(bookingId);
            newlyCreatedBookingIds.push(bookingId);
          }
        }

        if (!hasConflict) {
          const eventData = await pb.collection("events").create({
            name: booking?.name,
            description: booking?.description,
            moreInfoUrl: booking?.moreInfoUrl,
            bookings: bookingIds,
            location: currentLocation.value,
          });
          eventIds.push(eventData.id);
          newlyCreatedEventIds.push(eventData.id);
        }
      }
    }

    if (hasConflict) {
      // Restore updated bookings to their original state
      for (const id of updatedBookingIds) {
        const original = originalBookingSnapshots.get(id);
        if (original) {
          try {
            await pb.collection("bookings").update(id, {
              startTime: original.startTime,
              duration: original.duration,
              area: original.area,
              activity: original.activity,
              discount: original.discount,
              due: original.due,
              value: original.value,
              state: original.state,
              owner: original.owner,
              visitorEmail: original.visitorEmail,
              numberInParty: original.numberInParty,
            });
          } catch (e) {
            console.error(`Failed to restore booking ${id}:`, e);
          }
        }
      }
      // Delete newly created bookings
      await rollbackBookings(newlyCreatedBookingIds);
      // Delete newly created events
      for (const eid of newlyCreatedEventIds) {
        try {
          await pb.collection("events").update(eid, { state: "Deleted" });
        } catch (e) {
          console.error(`Failed to rollback event ${eid}:`, e);
        }
      }
    } else {
      // No conflicts — apply deferred cleanup
      for (const unusedId of deferredUnusedBookings) {
        await pb.collection("bookings").update(unusedId, { state: "Deleted" });
      }
      for (const { eventId: eid, bookingIds } of deferredEventUpdates) {
        await pb.collection("events").update(eid, {
          bookings: bookingIds,
          name: booking?.name,
          description: booking?.description,
          moreInfoUrl: booking?.moreInfoUrl,
        });
      }

      // Remove any remaining events and their bookings that no longer match a recurring date
      for (const [, remainingEvent] of eventsByDate) {
        const remainingBookings = remainingEvent?.expand?.bookings || [];
        for (const b of remainingBookings) {
          await pb.collection("bookings").update(b.id, { state: "Deleted" });
        }
        await pb.collection("events").update(remainingEvent.id, { state: "Deleted" });
      }

      // Clean up orphaned events that had no bookings
      for (const oid of orphanedEventIds) {
        await pb.collection("events").update(oid, { state: "Deleted" });
      }

      const updatedRecurringEvent = await pb.collection("recurringEvents").update(
        recurringId as string,
        {
          events: eventIds,
          endDate,
          days: booking?.daysString,
          frequency: booking?.recurring?.frequency,
          interval: booking?.recurring?.interval,
        },
        {
          expand: "events.bookings.docketLine.docket",
        }
      );

      if (
        updatedRecurringEvent &&
        updatedRecurringEvent?.expand?.events?.length
      ) {
        const { id, events, frequency, interval, days, endDate } =
          updatedRecurringEvent;
        const editEventData = {
          ...updatedRecurringEvent?.expand?.events?.[0],
          recurring: {
            recurringId: id,
            events,
            frequency,
            interval,
            days,
            endDate,
          },
        };
        handleEventEdit(editEventData);
      }
    }

    if (errorDates.length) {
      console.error("Errors occurred on the following dates:", errorDates);
      isBookingWarningDialogVisible.value = true;
      errorDateList.value = Array.from(new Set(errorDates));
    }
  };

  const handleRecurringEvent = async (booking: any) => {
    errorDateList.value = [];
    const errorDates: string[] = []; // Initialize error tracking list
    let allBookingsSuccessful = true; // Flag to track overall booking success

    const startTime = moment(
      `${booking?.date} ${booking?.time}`,
      "YYYY-MM-DD HH:mm"
    ).toISOString();

    try {
      // Function to create bookings for all selected areas and dates
      const handleRecurringBookingCreation = async (
        booking: any,
        recurringConfigDates: string[]
      ) => {
        // Flatten the areas and activities into a single list of booking combinations
        const bookingCombinations = Object.entries(
          booking.activity as Record<string, string[]>
        )?.flatMap(([activityId, areaIds]) =>
          areaIds?.map((areaId) => ({ areaId, activityId }))
        );

        const durationTotal = await mapSelectionToMinutes(
          String(booking?.duration)?.replace(":", ".")
        );

        let eventIds: string[] = [];

        // Track all created bookings across all dates for rollback
        const allCreatedBookingIds: string[] = [];

        // Create individual bookings for each combination of date, area, and activity
        for (const bookingDate of recurringConfigDates) {
          if (!allBookingsSuccessful) break;
          let bookingIds: string[] = [];

          // Create bookings for each area-activity combination on this date
          for (const { areaId, activityId } of bookingCombinations) {
            // Calculate the due and value in one go to avoid redundancy
            const bookingSpacing =
              activityMap.value[activityId]?.bookingSpacing;
            const sessionFullPrice =
              activityMap.value[activityId]?.sessionFullPrice;

            const calculatedValue = booking?.trackTickets
              ? 0
              : (durationTotal / bookingSpacing) * sessionFullPrice;

            // Make sure areaId comes first and activityId comes second
            const bookingParams = {
              startTime: moment(
                `${bookingDate} ${booking.time}`,
                "YYYY-MM-DD HH:mm"
              ).toISOString(),
              duration: durationTotal,
              area: areaId,
              activity: activityId,
              due: calculatedValue, // Reusing the calculated value
              value: calculatedValue, // Reusing the calculated value
              state: "Confirmed",
              visitorEmail: booking.visitorEmail,
              owner: booking?.owner,
              numberInParty: bookingIds.length === 0 ? booking.numberInParty : undefined,
              createdBy: booking?.loggedInUser,
              source: "Event",
            };

            try {
              // Create the booking
              const createdBooking = await pb.collection("bookings").create(bookingParams);
              bookingIds.push(createdBooking.id);
              allCreatedBookingIds.push(createdBooking.id);
            } catch (e) {
              console.error(
                `Error creating booking for ${bookingDate} in area ${areaId} and activity ${activityId}:`,
                e
              );
              errorDates.push(bookingDate);
              allBookingsSuccessful = false;
              break;
            }
          }

          if (!allBookingsSuccessful) break;

          try {
            const eventData = await pb.collection("events").create({
              name: booking?.name,
              bookings: bookingIds,
              description: booking?.description,
              moreInfoUrl: booking?.moreInfoUrl,
              trackTickets: booking?.trackTickets,
              location: currentLocation.value,
            });
            eventIds.push(eventData.id);
          } catch (e) {
            console.error(`Error creating event for ${bookingDate}:`, e);
            errorDates.push(bookingDate);
            allBookingsSuccessful = false;
          }
        }

        // If any date failed, roll back all created bookings and events
        if (!allBookingsSuccessful) {
          await rollbackBookings(allCreatedBookingIds);
          for (const eid of eventIds) {
            try {
              await pb.collection("events").update(eid, { state: "Deleted" });
            } catch (e) {
              console.error(`Failed to rollback event ${eid}:`, e);
            }
          }
          eventIds.length = 0;
        }

        // Only create recurring event if all bookings and events were successful
        if (allBookingsSuccessful) {
          try {
            const newRecurringEvent = await pb
              .collection("recurringEvents")
              .create(
                {
                  events: [...eventIds],
                  days: booking?.daysString,
                  endDate: booking?.recurring?.endDate,
                  frequency: booking?.recurring?.frequency,
                  interval: booking?.recurring?.interval,
                },
                {
                  expand: "events.bookings.docketLine.docket",
                }
              );

            // update earlier event id into store
            eventId.value = eventIds[0];

            // Update recurring eventId to store
            recurringEventId.value = newRecurringEvent.id;

            if (
              newRecurringEvent &&
              newRecurringEvent?.expand?.events?.length
            ) {
              const { id, events, frequency, interval, days, endDate } =
                newRecurringEvent;
              const editEventData = {
                ...newRecurringEvent?.expand?.events[0],
                recurring: {
                  recurringId: id,
                  events,
                  frequency,
                  interval,
                  days,
                  endDate,
                },
              };
              handleEventEdit(editEventData);
            }

            // update products
            if (booking?.ticketsConfig?.length) {
              for (const item of booking.ticketsConfig) {
                const inStock = Math.max(
                  item.ticketLimit - findSoldTicket.value(item.product),
                  0
                );
                await pb.collection("products").update(item.product, {
                  eventTicketLimit: item.ticketLimit,
                  event: eventId.value,
                  eventBarValue: item.eventBarValue,
                  eventDate: startTime,
                  inStock,
                });
              }
            }
          } catch (e) {
            console.error("Error saving recurring event:", e);
            allBookingsSuccessful = false;
          }
        }
      };

      // Check if the booking is a recurring booking
      if (!isEmpty(booking?.recurring)) {
        const { id, days, endDate, frequency, interval, isEditAll, startDate } =
          booking.recurring;
          console.log('id',id)

        const recurringItems = getSelectedRecurringItem.value(interval);
        const daysString = days
          .map((dayId: number) => recurringItems[dayId]?.value)
          .filter(Boolean)
          .join(", ");

        // Setup end date and initialize current date
        const endMoment = moment(endDate).startOf("day");
        let currentDate = startDate ? moment(startDate).startOf("day") : moment().startOf("day");
        const recurringConfigDates: any[] = [];

        // Generate valid booking dates based on the selected days
        while (currentDate.isSameOrBefore(endMoment)) {
          days.forEach((dayId: string) => {
            const dayOfWeek = recurringItems[dayId]?.value;
            if (dayOfWeek) {
              const targetDate = currentDate.clone().day(dayOfWeek);

              // Adjust to the next week's day if the target date is in the past
              const nextValidDate = targetDate.isBefore(currentDate)
                ? targetDate.add(1, "week")
                : targetDate;

              // Add the date to the list if it's within the end date range
              if (nextValidDate.isSameOrBefore(endMoment)) {
                recurringConfigDates.push(nextValidDate.format("YYYY-MM-DD"));
              }
            }
          });
          currentDate
            .add(frequency, interval === "Weeks" ? "week" : "month")
            .startOf("day");
        }

        // If this is an existing recurring booking
        if (id) {
          try {
            const recurringEventsRecords = await pb
              .collection("recurringEvents")
              .getOne(id, { expand: "events.bookings" });

            // Filter out deleted events so they don't interfere with the update
            if (recurringEventsRecords?.expand?.events) {
              recurringEventsRecords.expand.events = recurringEventsRecords.expand.events.filter(
                (ev: any) => ev?.state !== "Deleted"
              );
            }

            if (recurringEventsRecords?.expand?.events?.length) {
              if (isEditAll) {
                // Recompute dates from the earliest existing event so we don't lose events before the clicked one
                const earliestStartTime = recurringEventsRecords.expand.events
                  .flatMap((ev: any) => ev?.expand?.bookings?.map((b: any) => b.startTime) || [])
                  .filter(Boolean)
                  .sort()[0];

                if (earliestStartTime) {
                  const seriesStart = moment(earliestStartTime).startOf("day");
                  if (seriesStart.isBefore(moment(startDate).startOf("day"))) {
                    recurringConfigDates.length = 0;
                    let cur = seriesStart.clone();
                    while (cur.isSameOrBefore(endMoment)) {
                      days.forEach((dayId: string) => {
                        const dayOfWeek = recurringItems[dayId]?.value;
                        if (dayOfWeek) {
                          const target = cur.clone().day(dayOfWeek);
                          const nextValid = target.isBefore(cur) ? target.add(1, "week") : target;
                          if (nextValid.isSameOrBefore(endMoment)) {
                            recurringConfigDates.push(nextValid.format("YYYY-MM-DD"));
                          }
                        }
                      });
                      cur.add(frequency, interval === "Weeks" ? "week" : "month").startOf("day");
                    }
                  }
                }
                console.log('here', booking)
                // Handle updating all records (if editing all)
                await handleRecurringEventUpdate(
                  { ...booking, daysString },
                  recurringConfigDates,
                  recurringEventsRecords,
                  id,
                  endDate
                );
              } else {
                // Handle updating a single record
                await updateEvent(booking);
                await refetchEvent();
              }
            }
          } catch (e) {
            console.error("Error fetching recurring bookings for update:", e);
            allBookingsSuccessful = false;
          }
        } else {
          // If no recurring booking exists, create a new one
          await handleRecurringBookingCreation(
            { ...booking, daysString },
            recurringConfigDates
          );
        }
      }
    } catch (e) {
      console.error("Error handling recurring booking:", e);
      allBookingsSuccessful = false;
    }

    // Handle error notifications or logging if errors occurred
    if (errorDates.length) {
      isBookingWarningDialogVisible.value = true;
      errorDateList.value = Array.from(new Set(errorDates)); // Convert Set back to array
    }

    if (!allBookingsSuccessful) {
      console.log(
        "Some bookings failed, not creating events or recurring events."
      );
    }

    console.error("Error dates:", errorDates);
  };

  /**
   * Fetches the list of ticket products from the database that can be bought in the webshop.
   *
   * @returns {Promise<any[]>} - A promise that resolves to an array of ticket products.
   * If the request fails or if the response does not contain the expected data, the promise will resolve to `undefined`.
   */
  const getTicketProducts = async (eventId: string) => {
    try {
      const resultList = await pb.collection("products").getFullList({
        filter: `event = "${eventId}" && inactive = false`,
        fields: "collectionId,name,id,event,eventTicketLimit,eventBarValue,sellPrice,sortOrder,description,maxDiscount,memberBookingDiscountApplies,notDirectlySold,canBuyInWebshop,canBuyInKiosk,canPayInStore,btnTextColor,btnBackColor,productImage,reportingGroup,salesEnd",
      });

      const totalTicketCount = resultList.reduce((e, o) => e + o.eventTicketLimit, 0);

      return {
        ticketConfigs: resultList,
        totalTicket: totalTicketCount,
      };
    } catch (error) {
      console.error("Error fetching ticket products:", error);
      return {
        ticketConfigs: [],
      };
    }
  };

  return {
    paymentOptions,
    daysOfWeek,
    recurringWeeks,
    alternativeAreaList,
    recurringEndDateConfig,
    activityMap,
    getRecurringDates,
    getDuration,
    reserveSlot,
    updateEvent,
    confirmDelete,
    confirmDeleteAllRecurring,
    removeExistingPendingBooking,
    refetchEvent,
    handleRecurringEvent,
    getTicketProducts,
  };
};
